/**
 * ============================================================================
 * BACKGROUND AUDIO KEEP-ALIVE HELPER - iOS WKWebView
 * ============================================================================
 * 
 * On iOS, WKWebView suspends the AudioContext and JavaScript execution when
 * the app goes to background — UNLESS there is an active <audio> or <video>
 * HTML element playing. Since Hearen uses Web Audio API (BufferSourceNode)
 * for playback on iOS (not <audio> elements), we need this helper to keep
 * the audio session alive.
 * 
 * This helper creates a silent <audio> element that plays in loop. When
 * activated, it tricks iOS into thinking there is active media playback,
 * which prevents the AudioContext from being suspended in the background.
 * 
 * Usage:
 *   backgroundAudioHelper.activate();  // Call in user gesture context
 *   backgroundAudioHelper.deactivate(); // Call when all audio stops
 * 
 * ============================================================================
 */

const isCapacitorNative = () => {
  if (typeof window === 'undefined') return false;
  return window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
};

// Detect any iOS device (Safari mobile + Capacitor)
const isIOSAny = () => {
  if (typeof window === 'undefined') return false;
  if (isCapacitorNative()) return true;
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Generate a silent WAV file as a Blob URL.
 * 1 second of silence at 22050Hz, 16-bit mono PCM.
 * ~44KB — loaded once and reused.
 */
function createSilentWavUrl() {
  const sampleRate = 22050;
  const numSamples = sampleRate; // 1 second
  const numChannels = 1;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const dataSize = numSamples * numChannels * bytesPerSample;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF header
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');

  // fmt sub-chunk
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // Sub-chunk size
  view.setUint16(20, 1, true);  // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bitsPerSample, true);

  // data sub-chunk (all zeros = silence)
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  // ArrayBuffer is zero-initialized — pure silence

  const blob = new Blob([buffer], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}

class BackgroundAudioHelper {
  constructor() {
    this.audioElement = null;
    this.blobUrl = null;
    this.isActive = false;
  }

  /**
   * Activate the keep-alive audio element.
   * MUST be called within a user gesture context on iOS.
   * Idempotent — safe to call multiple times.
   */
  activate() {
    if (!isIOSAny()) return;
    if (this.isActive) return;

    console.log('[BackgroundAudio] Activating keep-alive...');

    // Create blob URL once
    if (!this.blobUrl) {
      this.blobUrl = createSilentWavUrl();
    }

    // Create audio element once
    if (!this.audioElement) {
      this.audioElement = new Audio();
      this.audioElement.loop = true;
      // Use minimal non-zero volume — iOS may ignore volume=0
      this.audioElement.volume = 0.01;
      this.audioElement.src = this.blobUrl;
    }

    this.audioElement.play()
      .then(() => {
        this.isActive = true;
        console.log('[BackgroundAudio] Keep-alive ACTIVE');
      })
      .catch(e => {
        console.warn('[BackgroundAudio] Failed to activate:', e.message);
      });
  }

  /**
   * Deactivate the keep-alive audio element.
   * Call when all audio playback has stopped.
   */
  deactivate() {
    if (!this.isActive || !this.audioElement) return;

    console.log('[BackgroundAudio] Deactivating keep-alive');
    this.audioElement.pause();
    this.isActive = false;
  }

  /**
   * Re-activate after returning from background.
   * iOS may have paused the <audio> element.
   */
  ensureActive() {
    if (!isIOSAny()) return;
    if (!this.audioElement || !this.isActive) return;

    if (this.audioElement.paused) {
      console.log('[BackgroundAudio] Re-activating paused keep-alive');
      this.audioElement.play().catch(() => {});
    }
  }

  /**
   * Cleanup all resources.
   */
  destroy() {
    this.deactivate();
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.audioElement = null;
  }
}

export const backgroundAudioHelper = new BackgroundAudioHelper();
export default backgroundAudioHelper;
